import * as React from "react"
import { useEffect } from "react"
import { useSignal } from "@preact/signals-react"
import { Alert, Grid, MenuItem, Typography } from "@mui/material"
import { AutocompleteSeeker, ButtonSeeker, IconSeeker, ModalSeeker, TextFieldSeeker } from "@ui-seeker/core"

import { NEWS_MANAGEMENT_ROUTES, STATE_FILTER_OPTIONS } from "@constants"
import { useToast } from "@hooks"
import { customerID, isSystemBusy } from "@signals"
import PageWrapper from "Pages/@PageWrapper"
import AnnouncementModal from "./@components/AnnouncementModal"
import AnnouncementsTable from "./@components/AnnouncementsTable"
import { useAnnouncements } from "./@hooks"
import { announcements, audiences, listFilters, openCreateModal, resetNewsManagement } from "./@signals"

export default function NewsManagementPage() {
    const { isLoadingTable, isSaving, reload, saveAnnouncement, toggleActive, removeAnnouncement } = useAnnouncements()
    const { showToast } = useToast()
    const pendingDelete = useSignal(null)

    useEffect(() => () => resetNewsManagement(), [])

    const setFilter = (field, value) => {
        listFilters.value = { ...listFilters.value, [field]: value }
        reload()
    }

    const handleDelete = () => {
        const row = pendingDelete.value
        removeAnnouncement(row.announcementID)
            .then(() => showToast.success("Announcement deleted"))
            .catch(() => showToast.error("It could not be deleted"))
            .finally(() => { pendingDelete.value = null })
    }

    const filters = listFilters.value

    const props = {
        head: `DIP Insights | ${NEWS_MANAGEMENT_ROUTES.ANNOUNCEMENTS_TITLE}`,
        title: NEWS_MANAGEMENT_ROUTES.ANNOUNCEMENTS_TITLE,
        subtitle: NEWS_MANAGEMENT_ROUTES.ANNOUNCEMENTS_SUBTITLE,
        hasErrors: isSystemBusy.value,
    }

    // The module already keeps this out of everyone else's menu, so reaching this line means
    // somebody typed the URL. Say why instead of showing an empty screen — the v2 endpoints
    // would answer 403 anyway. Same shape as Management › Pricing Config.
    if (customerID.value !== 0)
        // `hasErrors` forced off: the hook above has already fired its two requests and been
        // answered 403, which flips `isSystemBusy` — and the generic error page would bury
        // the one sentence that explains what is going on.
        return <PageWrapper {...props} actions={null} hasErrors={false}>
            <Alert severity="warning" variant="outlined">
                Announcements are published to other customers, so this screen is only available
                to the internal customer.
            </Alert>
        </PageWrapper>

    return (
        <PageWrapper
            {...props}
            actions={
                <ButtonSeeker variant="contained" startIcon={<IconSeeker icon="plus" />} onClick={openCreateModal}>
                    New announcement
                </ButtonSeeker>
            }
        >
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <AutocompleteSeeker
                        label="Customer"
                        placeholder="Any customer"
                        options={audiences.value.customers.map(c => ({ value: c.customerID, label: c.name }))}
                        value={filters.customerID}
                        onChange={e => setFilter("customerID", e.target.value || null)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <AutocompleteSeeker
                        label="User"
                        placeholder="Any user"
                        options={audiences.value.users.map(u => ({ value: u.userID, label: u.username }))}
                        value={filters.userID}
                        onChange={e => setFilter("userID", e.target.value || null)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextFieldSeeker
                        select
                        label="State"
                        value={filters.state ?? ""}
                        onChange={e => setFilter("state", e.target.value || null)}
                    >
                        <MenuItem value="">Any state</MenuItem>
                        {STATE_FILTER_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </TextFieldSeeker>
                </Grid>
            </Grid>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Filtering by customer or user also keeps the announcements addressed to everyone,
                since those reach them too.
            </Typography>

            <AnnouncementsTable
                data={announcements.value}
                audiences={audiences.value}
                isLoading={isLoadingTable}
                onToggleActive={(id, active) =>
                    toggleActive(id, active).catch(() => showToast.error("The change could not be saved"))
                }
                onDelete={row => { pendingDelete.value = row }}
            />

            <AnnouncementModal isSaving={isSaving} onSave={saveAnnouncement} />

            <ModalSeeker
                isOpen={!!pendingDelete.value}
                controller={() => { pendingDelete.value = null }}
                title="Delete this announcement?"
                successText="Delete"
                containerProps={{ maxWidth: "xs" }}
                successProps={{ onClick: handleDelete, color: "error" }}
            >
                <Typography variant="body2">
                    “{pendingDelete.value?.title}” and its record of who read it are deleted for good.
                    To simply stop showing it, retract it instead — that keeps the history.
                </Typography>
            </ModalSeeker>
        </PageWrapper>
    )
}
